import { ServiceResponse, ServiceResponseNoContent } from "../../models/serviceResponse";
import { ValidationError } from "../../models/validationError";
import { IApiClient } from "../interfaces/apiClient";
import { ServiceResponseFactory } from "../../helpers/serviceResponseFactory";

class ApiClient implements IApiClient {

    protected async Send<TInput, TOutput>(method: string, url: string, content: TInput = null, accessToken: string): Promise<ServiceResponse<TOutput> | Promise<ServiceResponseNoContent>> {

        const headers = new Headers();
        headers.append("Authorization", `Bearer ${accessToken}`);
        headers.append("Content-Type", "application/json");
        headers.append("Accept", "application/json");

        let body = null;

        if (content) {
            body = JSON.stringify(content);
        }

        const requestOptions: RequestInit = {
            method: method,
            headers: headers,
            body: body,
            redirect: 'follow'
        };

        const apiResponse = await fetch(url, requestOptions);

        if (apiResponse.ok) {
            if (apiResponse.status === 204) {
                return ServiceResponseFactory.SuccessfulResponseNoContent();
            }

            var responseT = (await apiResponse.json()) as TOutput;
            return ServiceResponseFactory.SuccessfulResponse(responseT);
        }
        else {
            let serviceResponse: ServiceResponse<TOutput>;
            let errorMessage = apiResponse.statusText;

            if (errorMessage.length === 0) {
                errorMessage = "Unable to process request.";
            }

            if (apiResponse.status === 400) {
                const validationErrors = (await apiResponse.json()) as ValidationError[];
                serviceResponse = ServiceResponseFactory.FailedValidationResponse(validationErrors,  errorMessage)
            }
            else if (apiResponse.status === 401) {
                serviceResponse = ServiceResponseFactory.FailedAuthenticationResponse(errorMessage);
            }
            else if (apiResponse.status === 403) {
                serviceResponse = ServiceResponseFactory.FailedAuthorizationResponse(errorMessage);
            }
            else if (apiResponse.status === 404) {
                serviceResponse = ServiceResponseFactory.FailedNotFoundResponse(errorMessage);
            }
            else if (apiResponse.status === 409) {
                serviceResponse = ServiceResponseFactory.FailedConflictResponse(errorMessage);
            }
            else {
                serviceResponse = ServiceResponseFactory.FailedSystemErrorResponse(errorMessage);
            }

            return serviceResponse;
        }
    }

    public async Get<TOutput>(baseUrl: string, resourceUrl: string, accessToken: string): Promise<ServiceResponse<TOutput>> {
        const getResponse = await this.Send<TOutput, TOutput>("Get", `${baseUrl}\\${resourceUrl}`, null, accessToken);
        return getResponse as ServiceResponse<TOutput>;
    }

    public async Put<TInput, TOutput>(baseUrl: string, resourceUrl: string, content: TInput, accessToken: string): Promise<ServiceResponse<TOutput>> {
        const putResponse = await this.Send<TInput, TOutput>("Put", `${baseUrl}\\${resourceUrl}`, content, accessToken);
        return putResponse as ServiceResponse<TOutput>;
    }

    public async PutNoResponse<TInput>(baseUrl: string, resourceUrl: string, content: TInput, accessToken: string): Promise<ServiceResponseNoContent> {
        const putResponse = await this.Send<TInput, null>("Put", `${baseUrl}\\${resourceUrl}`, content, accessToken);
        return putResponse as ServiceResponseNoContent;
    }

    public async Post<TInput, TOutput>(baseUrl: string, resourceUrl: string, content: TInput, accessToken: string): Promise<ServiceResponse<TOutput>> {
        const postResponse = await this.Send<TInput, TOutput>("Post", `${baseUrl}\\${resourceUrl}`, content, accessToken);
        return postResponse as ServiceResponse<TOutput>;
    }

    public async PostNoResponse<TInput>(baseUrl: string, resourceUrl: string, content: TInput, accessToken: string): Promise<ServiceResponseNoContent> {
        const postResponse = await this.Send<TInput, null>("Post", `${baseUrl}\\${resourceUrl}`, content, accessToken);
        return postResponse as ServiceResponseNoContent;
    }

    public async Patch<TInput, TOutput>(baseUrl: string, resourceUrl: string, content: TInput, accessToken: string): Promise<ServiceResponse<TOutput>> {
        const patchResponse = await this.Send<TInput, TOutput>("Patch", `${baseUrl}\\${resourceUrl}`, content, accessToken);
        return patchResponse as ServiceResponse<TOutput>;
    }

    public async Delete(baseUrl: string, resourceUrl: string, accessToken: string): Promise<ServiceResponseNoContent> {
        const deleteResponse = await this.Send<string, string>("Delete", `${baseUrl}\\${resourceUrl}`, null, accessToken);
        return deleteResponse as ServiceResponseNoContent;
    }
};

export { ApiClient };